import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  Refresh,
  Android,
  Apple,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';
import FeaturedAppForm from './FeaturedAppForm';
import { getImageUrlWithFallback } from '../../utils/imageUrlHelper';

interface FeaturedApp {
  _id: string;
  name: string;
  description: string;
  iconUrl: string;
  androidUrl?: string;
  iosUrl?: string;
  isActive: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

const FeaturedAppsList: React.FC = () => {
  const [apps, setApps] = useState<FeaturedApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState<boolean | null>(null);
  const [selectedApp, setSelectedApp] = useState<FeaturedApp | null>(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [appToDelete, setAppToDelete] = useState<FeaturedApp | null>(null);

  const fetchApps = async () => {
    try {
      setLoading(true);
      setError('');
      const filters: any = {};
      if (filterActive !== null) {
        filters.isActive = filterActive;
      }
      if (searchTerm) {
        filters.search = searchTerm;
      }
      const response = await apiService.getAllFeaturedApps(filters);
      if (response.success && response.data) {
        const sortedApps = [...response.data].sort((a, b) => a.displayOrder - b.displayOrder);
        setApps(sortedApps);
      } else {
        setError(response.error || 'Failed to fetch featured apps');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch featured apps');
      toast.error('Failed to fetch featured apps');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApps();
  }, [filterActive]);

  const handleCreate = () => {
    setSelectedApp(null);
    setFormDialogOpen(true);
  };

  const handleEdit = (app: FeaturedApp) => {
    setSelectedApp(app);
    setFormDialogOpen(true);
  };

  const handleFormClose = () => {
    setFormDialogOpen(false);
    setSelectedApp(null);
  };

  const handleFormSuccess = () => {
    handleFormClose();
    fetchApps();
  };

  const handleDeleteClick = (app: FeaturedApp) => {
    setAppToDelete(app);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!appToDelete) return;

    try {
      const response = await apiService.deleteFeaturedApp(appToDelete._id);
      if (response.success) {
        toast.success('Featured app deleted successfully');
        fetchApps();
      } else {
        toast.error(response.error || 'Failed to delete featured app');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete featured app');
    } finally {
      setDeleteDialogOpen(false);
      setAppToDelete(null);
    }
  };

  const handleToggleActive = async (app: FeaturedApp) => {
    try {
      const response = await apiService.toggleFeaturedAppActive(app._id);
      if (response.success) {
        toast.success(`Featured app ${app.isActive ? 'deactivated' : 'activated'} successfully`);
        fetchApps();
      } else {
        toast.error(response.error || 'Failed to toggle featured app status');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to toggle featured app status');
    }
  };

  const handleSearch = () => {
    fetchApps();
  };

  const filteredApps = apps.filter((app) => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        app.name.toLowerCase().includes(search) ||
        app.description.toLowerCase().includes(search)
      );
    }
    return true;
  });

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Featured Apps Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={handleCreate}
          >
            Add Featured App
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchApps}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Search by name or description"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={filterActive === true}
                  onChange={(e) => setFilterActive(e.target.checked ? true : null)}
                />
              }
              label="Active Only"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              onClick={handleSearch}
              fullWidth
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Apps Count */}
          <Typography variant="body2" color="text.secondary" mb={2}>
            {filteredApps.length} featured app{filteredApps.length !== 1 ? 's' : ''} found
          </Typography>

          {/* Apps Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Icon</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="center">Platforms</TableCell>
                  <TableCell align="center">Display Order</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredApps.map((app) => (
                  <TableRow key={app._id}>
                    <TableCell>
                      <img
                        src={getImageUrlWithFallback(app.iconUrl)}
                        alt={app.name}
                        style={{
                          width: 56,
                          height: 56,
                          objectFit: 'cover',
                          borderRadius: 8,
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/placeholder-icon.png';
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {app.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {app.description.length > 100
                          ? `${app.description.substring(0, 100)}...`
                          : app.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={1} justifyContent="center">
                        {app.androidUrl && (
                          <Chip
                            icon={<Android />}
                            label="Android"
                            color="success"
                            size="small"
                          />
                        )}
                        {app.iosUrl && (
                          <Chip
                            icon={<Apple />}
                            label="iOS"
                            color="primary"
                            size="small"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{app.displayOrder}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={app.isActive ? 'Click to deactivate' : 'Click to activate'}>
                        <IconButton
                          onClick={() => handleToggleActive(app)}
                          color={app.isActive ? 'success' : 'default'}
                          size="small"
                        >
                          {app.isActive ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </Tooltip>
                      <Chip
                        label={app.isActive ? 'Active' : 'Inactive'}
                        color={app.isActive ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={1} justifyContent="center">
                        <Tooltip title="Edit">
                          <IconButton
                            onClick={() => handleEdit(app)}
                            color="primary"
                            size="small"
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            onClick={() => handleDeleteClick(app)}
                            color="error"
                            size="small"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredApps.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No featured apps found. Click "Add Featured App" to create one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Form Dialog */}
      <Dialog
        open={formDialogOpen}
        onClose={handleFormClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedApp ? 'Edit Featured App' : 'Add Featured App'}
        </DialogTitle>
        <DialogContent>
          <FeaturedAppForm
            app={selectedApp}
            onSuccess={handleFormSuccess}
            onCancel={handleFormClose}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the featured app "{appToDelete?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeaturedAppsList;
